import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface NextUpInfo {
  /** Clean court label, e.g. "Cancha 1" */
  nextUpCourt: string | null;
  /** Category extracted from notification, e.g. "Masculino" */
  nextUpCategory: string | null;
  /** Subcategory extracted from notification, e.g. "1era" or "A" */
  nextUpSubcategory: string | null;
  /** Phase extracted, e.g. "Grupos" or "Eliminación" */
  nextUpPhase: string | null;
}

/**
 * Parses notification messages of the form:
 *   "Eres el siguiente en jugar en Cancha 1 - Masculino 1era, Grupos. ¡Prepárate!"
 *
 * Backward compatible: older notifications without category/phase return nulls
 * for those fields, which consumers should treat as "applies to any view".
 */
function parseNextUpMessage(message: string): Omit<NextUpInfo, 'nextUpCourt'> & { nextUpCourt: string | null } {
  if (!message) {
    return { nextUpCourt: null, nextUpCategory: null, nextUpSubcategory: null, nextUpPhase: null };
  }

  // Court: "jugar en {court}" — court ends at " - " (new format) or "." (old format)
  const courtMatch = message.match(/jugar en\s+([^.\-]+?)(?:\s*[-.]|$)/i);
  const nextUpCourt = courtMatch ? courtMatch[1].trim() : null;

  // Metadata after " - ": "Categoria Subcategoria, Fase."
  // e.g. "Masculino 1era, Grupos" / "Mixto A, Eliminación"
  const metaMatch = message.match(/-\s*([^,]+?)\s*,\s*([^.]+?)\s*\./);
  let nextUpCategory: string | null = null;
  let nextUpSubcategory: string | null = null;
  let nextUpPhase: string | null = null;

  if (metaMatch) {
    const catSubcat = metaMatch[1].trim();
    const rawPhase = metaMatch[2].trim();
    nextUpPhase = normalizePhase(rawPhase);
    // Split "Masculino 1era" → category=Masculino, subcategory=1era
    // Split "Mixto A" → category=Mixto, subcategory=A
    const parts = catSubcat.split(/\s+/);
    if (parts.length >= 2) {
      nextUpCategory = parts[0];
      nextUpSubcategory = parts.slice(1).join(' ');
    } else {
      nextUpCategory = catSubcat;
    }
  }

  return { nextUpCourt, nextUpCategory, nextUpSubcategory, nextUpPhase };
}

/**
 * Normalize phase strings from notifications to canonical lowercase keys.
 * Supports both group/elimination general labels and specific round abbreviations.
 * Returns one of: 'grupos' | 'octavos' | 'cuartos' | 'semifinales' | 'final' | 'eliminacion' | original-lowercased.
 */
function normalizePhase(raw: string): string {
  if (!raw) return raw;
  const s = raw.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  if (s === 'qf' || s.includes('cuarto')) return 'cuartos';
  if (s === 'sf' || s.includes('semi')) return 'semifinales';
  if (s === 'r16' || s.includes('octavo')) return 'octavos';
  if (s === 'f' || s === 'final' || s.includes('final')) return 'final';
  if (s.startsWith('grupo')) return 'grupos';
  if (s.includes('elimin')) return 'eliminacion';
  return s;
}

export function useNextUpCourt(eventId: string | undefined): NextUpInfo {
  const { user } = useAuth();
  const [info, setInfo] = useState<NextUpInfo>({
    nextUpCourt: null,
    nextUpCategory: null,
    nextUpSubcategory: null,
    nextUpPhase: null,
  });
  const channelId = useRef(`next-up-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (!user?.id || !eventId) return;

    const fetchNextUp = async () => {
      const { data } = await supabase
        .from('notifications')
        .select('message')
        .eq('user_id', user.id)
        .eq('type', 'match_upcoming')
        .like('action_url', `%${eventId}%`)
        .eq('read', false)
        .order('created_at', { ascending: false })
        .limit(1);

      if (data && data.length > 0) {
        setInfo(parseNextUpMessage(data[0].message || ''));
      } else {
        setInfo({ nextUpCourt: null, nextUpCategory: null, nextUpSubcategory: null, nextUpPhase: null });
      }
    };

    fetchNextUp();

    const channel = supabase
      .channel(channelId.current)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'notifications',
        filter: `user_id=eq.${user.id}`,
      }, () => {
        fetchNextUp();
      })
      .subscribe();

    const poll = setInterval(fetchNextUp, 10000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(poll);
    };
  }, [user?.id, eventId]);

  return info;
}
